import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialog, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CustomDialogData } from '../../../core/models/custom-dialog.model';
import { TitresDTO } from '../../../core/models/emission.model';
import { EmissionService } from '../../../core/services/emission.service';
import { ToastService } from '../../../core/services/toast.service';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { take } from 'rxjs';

@Component({
    selector: 'app-titre-details',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        InputTextModule,
        InputNumberModule,
        SelectModule,
        DatePickerModule
    ],
    templateUrl: './titre-details.component.html',
    styleUrl: './titre-details.component.scss'
})
export class TitreDetailsComponent implements OnInit {
  private emissionService = inject(EmissionService);
  private toastService = inject(ToastService);
  private ref = inject(DynamicDialogRef);

  formGroup = new FormGroup({
    isin: new FormControl(''),
    typeInstrument: new FormControl('', { validators: [Validators.required] }),
    dueDate: new FormControl<Date | null>(null, { validators: [Validators.required] }),
    rate: new FormControl<number>(0, { validators: [Validators.required, Validators.min(0)] }),
    unitNominalValue: new FormControl<number>(0, { validators: [Validators.required, Validators.min(0)] })
  });

  boundData: CustomDialogData<{ emissionId: string }> | undefined;
  instance: DynamicDialog | undefined;
  emissionId: string = '';

  instrumentTypes = [
    { label: 'OAT (Obligation Assimilable du Trésor)', value: 'OAT' },
    { label: 'BAT (Bon Assimilable du Trésor)', value: 'BAT' }
  ];

  isView = false;

  ngOnInit() {
    this.boundData = this.instance?.data;

    // Mode ajout de titre à une émission
    if (this.boundData?.data && 'emissionId' in this.boundData.data) {
      this.emissionId = (this.boundData.data as any).emissionId;
    }

    // Mode consultation ou modification d'un titre existant
    if (this.boundData?.data && !('emissionId' in this.boundData.data)) {
      const titre = this.boundData.data as any;
      this.formGroup.patchValue({
        isin: titre.isin || '',
        typeInstrument: titre.typeInstrument,
        dueDate: titre.dueDate ? new Date(titre.dueDate) : null,
        rate: titre.rate,
        unitNominalValue: titre.unitNominalValue
      });

      // Mode consultation : désactiver tous les champs
      if (this.boundData.isView) {
        this.isView = true;
        this.formGroup.disable();
      }
    }
  }

  save() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.toastService.showToast('Attention', 'Veuillez remplir tous les champs obligatoires', 'warn');
      return;
    }

    const formValue = this.formGroup.getRawValue();

    const titre: TitresDTO = {
      isin: formValue.isin ? formValue.isin : undefined,
      typeInstrument: formValue.typeInstrument!,
      dueDate: this.formatDate(formValue.dueDate!),
      rate: formValue.rate!,
      unitNominalValue: formValue.unitNominalValue!
    };

    // Mode modification d'un titre existant
    if (this.boundData?.isEdit && this.boundData?.data && 'id' in this.boundData.data) {
      // Pour l'instant, on ne peut pas modifier un titre via l'API
      // Il faudrait ajouter un endpoint PUT dans le backend
      this.toastService.showToast('Info', 'La modification de titre n\'est pas encore supportée par l\'API', 'info');
      this.ref.close(true);
      return;
    }

    // Mode ajout de titre à une émission
    if (!this.emissionId) {
      this.toastService.showToast('Erreur', 'ID d\'émission manquant', 'error');
      return;
    }

    this.emissionService.addTitreToEmission(this.emissionId, titre)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastService.showToast('Succès', 'Titre ajouté avec succès', 'success');
          this.ref.close(true);
        },
        error: (error) => {
          this.toastService.showToast('Erreur', 'Erreur lors de l\'ajout du titre', 'error');
          console.error(error);
        }
      });
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
  }

  cancel() {
    this.ref.close();
  }
}
